import { type ComponentProps, type FC, forwardRef, type ReactNode, useMemo } from 'react';
import { Image, Thumbnail as RiffThumbnail } from '@yj-intl/riff-react';
import styled from 'styled-components';
import {
  getFallbackImageSrc,
  getImageWidthAndHeight,
  ImageSize,
  ImageSizeConstants,
  isMultiSrc,
  MultiSrc
} from './util';
import { usePictransErrorCheck } from './usePictransFallback';

export { ImageSizeConstants };

// ThumbnailV1との差分を吸収する
const ThumbnailWrapper = styled.div`
  display: block;
  width: var(--thumbnail-width, auto);
  height: var(--thumbnail-height, auto);

  .riff-Thumbnail__root {
    display: block;
    width: 100%;
    height: 100%;
  }

  .riff-Thumbnail__videoInformation {
    padding: 2px 4px;

    svg {
      width: 11px;
      height: 11px;
    }

    .riff-text-x-small {
      font-family: Helvetica;
      font-size: 11px;
      line-height: 11px;
    }
  }
`;

interface ThumbnailProps {
  readonly alt?: string;
  readonly src?: string | MultiSrc;
  readonly size?: ImageSize | ImageSizeConstants;
  readonly hasVideo: boolean;
  readonly videoDuration?: string;
  readonly loading?: 'lazy' | 'eager';
  readonly fallback?: ReactNode;
}

interface ThumbnailPropsWithoutVideo extends ThumbnailProps {
  readonly hasVideo: false;
  readonly videoDuration?: never;
}

interface ThumbnailPropsWithVideo extends ThumbnailProps {
  readonly hasVideo: true;
  readonly videoDuration?: string;
}

export type Props = ThumbnailPropsWithoutVideo | ThumbnailPropsWithVideo;

export const Thumbnail: FC<Props> = props => {
  const source = props.src;

  const imageSize = getImageWidthAndHeight(props.size);
  const fallbackSrc = getFallbackImageSrc(props.size);

  const { imgRef, isError } = usePictransErrorCheck();

  // 親要素からの上書きを可能にするため、CSS変数を使用
  const cssSizeOverride = useMemo(
    () =>
      ({
        '--thumbnail-width': imageSize?.width,
        '--thumbnail-height': imageSize?.height
      } as React.CSSProperties),
    [imageSize?.width, imageSize?.height]
  );

  const htmlWidth = toHtmlSize(imageSize?.width);
  const htmlHeight = toHtmlSize(imageSize?.height);
  const width = htmlWidth === undefined ? 'full' : undefined;
  const height = htmlHeight === undefined ? 'full' : undefined;

  if (isError) {
    // fallbackが指定されている場合はそれを返す（undefinedの場合はデフォルトのトルツメ画像、nullの場合はnullを返す）
    if (props.fallback !== undefined) {
      return props.fallback;
    }

    return (
      <ThumbnailWrapper style={cssSizeOverride}>
        <RiffThumbnail
          display="block"
          alt={props.alt}
          src={fallbackSrc}
          htmlHeight={htmlHeight}
          htmlWidth={htmlWidth}
          width={width}
          height={height}
          isVideo={props.hasVideo}
          videoDuration={props.videoDuration}
          objectFit="contain"
          loading={props.loading ?? 'lazy'}
        />
      </ThumbnailWrapper>
    );
  }

  if (!isMultiSrc(source)) {
    return (
      <ThumbnailWrapper style={cssSizeOverride}>
        <RiffThumbnail
          display="block"
          ref={imgRef}
          alt={props.alt}
          src={source}
          htmlHeight={htmlHeight}
          htmlWidth={htmlWidth}
          width={width}
          height={height}
          fallbackSrc={fallbackSrc}
          isVideo={props.hasVideo}
          videoDuration={props.videoDuration}
          objectFit="contain"
          loading={props.loading ?? 'lazy'}
        />
      </ThumbnailWrapper>
    );
  }

  return (
    <ThumbnailWrapper style={cssSizeOverride}>
      <RiffThumbnail
        display="block"
        ref={imgRef}
        alt={props.alt}
        as={Picture}
        htmlHeight={htmlHeight}
        htmlWidth={htmlWidth}
        width={width}
        height={height}
        src={source.jpeg}
        fallbackSrc={fallbackSrc}
        isVideo={props.hasVideo}
        videoDuration={props.videoDuration}
        objectFit="contain"
        loading={props.loading ?? 'lazy'}
        source={source}
      />
    </ThumbnailWrapper>
  );
};

const Picture = forwardRef<
  HTMLImageElement,
  ComponentProps<typeof RiffThumbnail> & {
    source: MultiSrc;
  }
>(({ source, ...pictureProps }, ref) => {
  return (
    <picture>
      <source srcSet={source.webp} type="image/webp" />
      <source srcSet={source.jpeg} type="image/jpeg" />
      <Image {...pictureProps} ref={ref} />
    </picture>
  );
});

const toHtmlSize = (size: string | undefined): number | undefined => {
  if (typeof size !== 'string') {
    return size;
  }

  // **pxとなっている場合は数値に変換する
  if (size.match(/^(:?0|[1-9]\d*)(px)?$/)) {
    return parseInt(size, 10);
  }

  // calcや%などのCSS値は無視
  return undefined;
};
